import { type ReactNode, useCallback, useEffect, useState } from 'react';
import AppMenu from './Navigation/AppMenu';
import { Grid, ThemeProvider } from '@mui/material';
import useStyles from './App.styles';
import Content from './App/Content';
import OnBoardingTour, { isOnBoardingShowable } from './App/OnBoarding/OnBoardingTour';
import { useWebUserContext, TourProvider, SelectedLinkProvider, HandleSelectedLinkChangeInputProps, AdminModeProvider, useAdminModeContext, SnackbarProvider } from 'contexts';
import Header from './App/Header';
import { useParams } from 'react-router';
import type { Link, RouteParams } from 'types';
import history, { navigateToSelectedChannelRoute } from 'utils/history';
import { ADMIN_LINK_CHANNEL_KEY } from 'utils/link';
// import { MessagePayload } from 'firebase/messaging';
// import { ENABLE_NOTIFICATIONS } from 'constants/env';
import AdminHeader from './App/Admin/AdminHeader';
import Routes from 'routes';
import { MainTheme, adminTheme } from 'theme';
import {useSmallDesktopScreen, useNoWebUserLinkSnackbar} from 'hooks';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

type AppPureProps = {
  children: ReactNode;
  HeaderComponent: () => JSX.Element | null;
};

const AppPure = ({ children, HeaderComponent }: AppPureProps): JSX.Element => {
  const classes = useStyles();

  return (
    <div className={classes.app}>
      <OnBoardingTour />
      <Grid container>
        <AppMenu />

        <Grid item className={classes.content}>
          <HeaderComponent />

          <Content>{children}</Content>
          <ToastContainer />

        </Grid>
      </Grid>
    </div>
  );
};

type GetSelectedLinkInputType = {
  links: Link[];
  selectedChannel: string | undefined;
};

const getSelectedLink = ({
  links,
  selectedChannel,
}: GetSelectedLinkInputType): Link | undefined => {
  if (selectedChannel) {
    return links.find(
      (_) => _.channelKey === selectedChannel && _.metadata.subscriptionEnabled,
    );
  }
};

type AppWithDataProps = {
  children: ReactNode;
};

const AppWithData = ({ children }: AppWithDataProps): JSX.Element => {
  const { webUser } = useWebUserContext();
  const { selectedChannel } = useParams<RouteParams>();
  const { isAdminMode, setIsAdminMode } = useAdminModeContext();

  const webUserLinks = webUser.links.filter(
    (link) => link.channelKey !== ADMIN_LINK_CHANNEL_KEY,
  );
  const selectedLinkFromUrl = getSelectedLink({
    links: webUserLinks,
    selectedChannel,
  });
  const isMobileDevice = useSmallDesktopScreen();

  const [selectedLink, setSelectedLink] = useState<Link | undefined>(
    selectedLinkFromUrl || undefined,
  );

  const showNoWebUserLinkSnackbar = useNoWebUserLinkSnackbar(selectedLink);

  const handleSelectedLinkChange = useCallback(
    ({
      isAdmin,
      newSelectedLink,
    }: HandleSelectedLinkChangeInputProps): void => {
      const newIsAdminMode = isAdmin && newSelectedLink.isAdmin;
      const route = newIsAdminMode ? Routes.ADMIN : Routes.HOME;

      navigateToSelectedChannelRoute(newSelectedLink, route);
      setIsAdminMode(newIsAdminMode);
      setSelectedLink(newSelectedLink);
    },
    [setIsAdminMode, setSelectedLink],
  );

  useEffect(() => {
    if (!selectedLinkFromUrl) {
      handleSelectedLinkChange({
        newSelectedLink: webUserLinks?.[0],
      });
    }

    if (webUserLinks.length === 0) {
      showNoWebUserLinkSnackbar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLink]);

  const HeaderComponent = isAdminMode ? AdminHeader : Header;

  return (
    <SelectedLinkProvider
      value={{ selectedLink, handleSelectedLinkChange }}
    >
      <TourProvider
        initialIsTourOpen={isOnBoardingShowable({
          isMobileDevice,
          selectedLink,
          webUser,
        })}
      >
        <AppPure HeaderComponent={HeaderComponent}>{children}</AppPure>
      </TourProvider>
    </SelectedLinkProvider>
  );
};

type AppProps = {
  children?: ReactNode;
};

const App = ({ children }: AppProps): JSX.Element => {
  // const { successSnackbar, warningSnackbar } = useSnackbar();

  const initialUrlIsAdmin =
    history.location.pathname.includes('administration');
  const [isAdminMode, setIsAdminMode] = useState<boolean>(initialUrlIsAdmin);

  // const handleNotificationMessageReceived = useCallback(
  //   (message: MessagePayload) => {
  //     if (ENABLE_NOTIFICATIONS) {
  //       if (message.notification) {
  //         successSnackbar(
  //           <>
  //             <h4>{message.notification.title}</h4>
  //             <div>{message.notification.body}</div>
  //           </>,
  //         );
  //       } else {
  //         warningSnackbar('FCM message sans notification reçu');
  //       }
  //     }
  //   },
  //   [successSnackbar, warningSnackbar],
  // );

  // useEffect(() => {
  //   if ('serviceWorker' in navigator) {
  //     navigator.serviceWorker.register({
  //       handleNotificationMessageReceived,
  //     });
  //   }
  // });

  return (
    <AdminModeProvider value={{ isAdminMode, setIsAdminMode }}>
      <ThemeProvider theme={isAdminMode ? adminTheme : MainTheme}>
        <SnackbarProvider>
          <AppWithData>
            {children}
          </AppWithData>
        </SnackbarProvider>
      </ThemeProvider>
    </AdminModeProvider>
  );
};

export default App;
